<template>
    <div class="solutions-page">
        <!-- 1. 页面主视觉 (Hero Section) -->
        <section class="solutions-hero">
            <div class="banner">
                <!-- 背景图片 -->
                <img src="@/assets/solution/banner.png" class="background-img" loading="lazy" alt="背景图">
            </div>
        </section>

        <!-- 2. 核心场景展示区 (Zig-zag 左右交替布局) -->
        <section class="section overflow-hidden">
            <!-- 替换为国际化标题 -->
            <h2 class="section-title text-center" v-scroll-reveal>{{ t('solutions.services.title') }}</h2>
            <div class="feature-rows">
                <div class="feature-row" v-for="(feature, index) in advancedFeatures" :key="index"
                    :class="{ 'reverse-row': index % 2 !== 0 }">
                    <!-- 文字部分 -->
                    <div class="feature-content" v-scroll-reveal
                        :class="index % 2 === 0 ? 'slide-right' : 'slide-left'">
                        <h3>{{ feature.title }}</h3>
                        <p>{{ feature.description }}</p>
                    </div>
                    <!-- 图片部分 -->
                    <img :src="getAssetImage(feature.icon)" class="feature-image" v-scroll-reveal.zoom
                        :class="index % 2 === 0 ? 'slide-left' : 'slide-right'" />
                </div>
            </div>
        </section>

        <!-- 3. 为什么选择我们 (优势网格) -->
        <section class="why-us-section bg-light">
            <div class="container">
                <!-- 替换为国际化标题 -->
                <h2 class="section-title">{{ t('solutions.whyUs.title') }}</h2>
                <div class="benefits-grid">
                    <div class="benefit-card" v-for="(benefit, index) in benefits" :key="index">
                        <h4>{{ benefit.title }}</h4>
                        <p>{{ benefit.desc }}</p>
                    </div>
                </div>
            </div>
        </section>

        <!-- 4. 底部行动呼吁 (CTA) -->
        <section class="cta-section">
            <!-- 替换为国际化文本 -->
            <h2>{{ t('solutions.cta.title') }}</h2>
            <p>{{ t('solutions.cta.desc') }}</p>
            <button class="btn-primary" @click="redirectToContact()">{{ t('solutions.cta.button') }}</button>
        </section>
    </div>
</template>

<script setup>
// ✨ 1. 引入 computed 和 useI18n
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

// --- 数据配置 ---

// ✨ 2. 使用 computed 包装场景解决方案数据
const advancedFeatures = computed(() => [
    {
        icon: 'card_hardware',
        title: t('solutions.services.features.hardware.title'),
        description: t('solutions.services.features.hardware.desc'),
    },
    {
        icon: 'card_software',
        title: t('solutions.services.features.software.title'),
        description: t('solutions.services.features.software.desc'),
    },
    {
        icon: 'card_server',
        title: t('solutions.services.features.server.title'),
        description: t('solutions.services.features.server.desc'),
    }
])

const imageModules = import.meta.glob('../assets/solution/*.png', { eager: true });

const redirectToContact = () => {
    window.open('/contact', '_blank')
}

const getAssetImage = (name) => {
    const globPath = `../assets/solution/${name}.png`;
    if (imageModules[globPath]) {
        return imageModules[globPath].default;
    } else {
        console.warn(`Icon ${name} not found, displaying default.`);
        return;
    }
}

// ✨ 3. 使用 computed 包装核心优势数据
const benefits = computed(() => [
    {
        title: t('solutions.whyUs.benefits.oneStop.title'),
        desc: t('solutions.whyUs.benefits.oneStop.desc')
    },
    {
        title: t('solutions.whyUs.benefits.custom.title'),
        desc: t('solutions.whyUs.benefits.custom.desc')
    },
    {
        title: t('solutions.whyUs.benefits.expandable.title'),
        desc: t('solutions.whyUs.benefits.expandable.desc')
    },
    {
        title: t('solutions.whyUs.benefits.deployment.title'),
        desc: t('solutions.whyUs.benefits.deployment.desc')
    }
])
</script>

<style scoped>
/* =========================================
   全局与基础样式
========================================= */
.solutions-page {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    color: #333;
    line-height: 1.6;
}

/* 1. 父容器 Banner 设置为相对定位 */
.banner {
    position: relative;
    width: 100%;
    height: 50vh;
    /* 假设是全屏 Banner */
    overflow: hidden;
}

/* 背景图处理同上 */
.background-img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    z-index: -1;
}

/* 2. **核心调整：使用绝对定位精确控制** */
.text-content {
    position: absolute;
    /* **关键调整：设置距离底部的距离（底部对齐）** */
    /* 调整这个值（例如 10%）可以控制文字的高低 */
    bottom: 50%;
    /* 水平居中方法 */
    left: 50%;
    transform: translateX(-50%);
    /* 水平偏移 50% 实现精确居中 */

    text-align: center;
    /* 文字内部居中 */
    color: white;
    z-index: 1;
    width: 90%;
    /* 给一个最大宽度，防止文字太多时贴边 */
}

/* 进一步精细化文字样式 */
.text-content h1 {
    font-size: 2.5rem;
    font-weight: bold;
    margin-bottom: 1rem;
    color: #000;
}

.text-content p {
    font-size: 1.2rem;
    font-weight: normal;
}

.placeholder-img {
    background-color: #eaeaea;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #999;
    font-size: 0.9rem;
}

.bg-light {
    background-color: #f9fafb;
}

.container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
}

.section-title {
    text-align: center;
    font-size: 2.2rem;
    margin-bottom: 50px;
    font-weight: 700;
}

/* 按钮通用样式 */
.btn-primary {
    background-color: #1a1a1a;
    color: #fff;
    border: none;
    padding: 14px 32px;
    border-radius: 30px;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.3s ease;
    font-weight: 600;
}

.btn-primary:hover {
    background-color: #333;
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
}

/* =========================================
   1. 主视觉 (Hero)
========================================= */
.solutions-hero {
    height: 50vh;
    min-height: 400px;
    /* 实际项目中这里可以放一张高级感的全屋智能背景图 */
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
}

.hero-content {
    max-width: 700px;
}

.hero-content h1 {
    font-size: 3rem;
    margin-bottom: 20px;
    color: #111;
    line-height: 1.2;
}

.hero-content p {
    font-size: 1.1rem;
    color: #555;
    margin-bottom: 30px;
}

/* =========================================
   2. 场景交替布局 (Zig-zag)
========================================= */
.scenarios-section {
    max-width: 1200px;
    margin: 0 auto;
    padding: 100px 20px;
    display: flex;
    flex-direction: column;
    gap: 120px;
}

.scenario-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 80px;
}

.scenario-row.reverse {
    flex-direction: row-reverse;
    /* 实现左右交替 */
}

.scenario-text {
    flex: 1;
}

.icon-wrapper {
    width: 50px;
    height: 50px;
    background: #f0f0f0;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    margin-bottom: 20px;
}

.scenario-text h2 {
    font-size: 2.2rem;
    margin-bottom: 20px;
    color: #111;
}

.scenario-text p {
    font-size: 1.1rem;
    color: #555;
    margin-bottom: 30px;
}

.feature-list {
    list-style: none;
    padding: 0;
    margin: 0 0 30px 0;
}

.feature-list li {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 12px;
    font-size: 1rem;
    color: #444;
}

.check {
    color: #10b981;
    font-weight: bold;
}

/* 绿色的对勾 */

.learn-more {
    color: #000;
    font-weight: 600;
    text-decoration: none;
    border-bottom: 2px solid #000;
    padding-bottom: 2px;
    transition: opacity 0.2s;
}

.learn-more:hover {
    opacity: 0.6;
}

.scenario-image {
    flex: 1;
    height: 450px;
    border-radius: 20px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08);
    width: 100%;
}

/* =========================================
   3. 优势网格 (Why Us)
========================================= */
.why-us-section {
    padding: 100px 0;
}

.benefits-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 30px;
}

.benefit-card {
    background: #fff;
    padding: 40px 30px;
    border-radius: 16px;
    text-align: center;
    border: 1px solid #eaeaea;
    transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.benefit-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.05);
}

.benefit-icon {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    margin: 0 auto 20px auto;
}

.benefit-card h4 {
    font-size: 1.2rem;
    margin-bottom: 15px;
}

.benefit-card p {
    font-size: 0.9rem;
    color: #666;
}

/* =========================================
   4. 行动呼吁 (CTA)
========================================= */
.cta-section {
    text-align: center;
    padding: 100px 20px;
    background-color: #111;
    color: #fff;
}

.cta-section h2 {
    font-size: 2.5rem;
    margin-bottom: 20px;
}

.cta-section p {
    font-size: 1.1rem;
    color: #aaa;
    margin-bottom: 40px;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
}

/* =========================================
   响应式适配
========================================= */
@media (max-width: 1024px) {
    .benefits-grid {
        grid-template-columns: repeat(2, 1fr);
    }

    .scenario-row,
    .scenario-row.reverse {
        gap: 40px;
    }

    .scenario-image {
        height: 350px;
    }
}

@media (max-width: 768px) {
    .hero-content h1 {
        font-size: 2.2rem;
    }

    /* 手机端取消左右交替，统一改为图片在上，文字在下 */
    .scenarios-section {
        gap: 80px;
        padding: 60px 20px;
    }

    .scenario-row,
    .scenario-row.reverse {
        flex-direction: column;
        text-align: left;
    }

    .scenario-image {
        width: 100%;
        height: 300px;
    }

    .benefits-grid {
        grid-template-columns: 1fr;
    }
}


.card {
    background: white;
    padding: 40px 30px;
    border-radius: 16px;
    text-align: center;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
}

.card-icon {
    width: 80px;
    height: 80px;
    margin: 0 auto 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    font-weight: bold;
}

.card h3 {
    margin-bottom: 15px;
    font-size: 1.3rem;
}

.card p {
    color: #666;
    font-size: 0.95rem;
}

.feature-rows {
    max-width: 1100px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 60px;
}

.feature-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 60px;
}

.feature-row.reverse-row {
    flex-direction: row-reverse;
}

.feature-content {
    flex: 1;
}

.feature-content h3 {
    font-size: 1.8rem;
    margin-bottom: 20px;
}

.feature-content p {
    color: #555;
    font-size: 1.1rem;
}

.feature-image {
    flex: 1;
    height: 350px;
    border-radius: 16px;
    width: 100%;
}
</style>